import { useState } from "react";
import { Link } from "wouter";
import { useListLeads, getListLeadsQueryKey, type Lead } from "@workspace/api-client-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { PaperBall } from "./CrumpledPaper";
import { Spinner } from "@/components/ui/spinner";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const BALL_ROTATIONS = [12, -28, 44, -17, 33, -8, 52, -41, 19, -26];

const COMPLETED_PARAMS = {
  status: "completed",
  limit: 50,
  sort: "recent",
  sort_dir: "desc",
} as const;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function formatCompletedDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function CompletedJobsDrawer({ open, onOpenChange }: Props) {
  const { data, isLoading } = useListLeads(COMPLETED_PARAMS, {
    query: {
      queryKey: getListLeadsQueryKey(COMPLETED_PARAMS),
      enabled: open,
    },
  });

  const leads = [...(data?.items ?? [])].sort((a, b) => {
    const ta = a.project?.completed_at ? new Date(a.project.completed_at).getTime() : 0;
    const tb = b.project?.completed_at ? new Date(b.project.completed_at).getTime() : 0;
    return tb - ta;
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 py-5 border-b glass-divider shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            Completed Jobs
            {data && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] text-xs font-semibold px-2 py-0.5 tabular-nums">
                {data.total}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner className="size-5" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
              <PaperBall size={40} rotation={22} />
              <p className="text-sm">No completed jobs yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/50">
              {leads.map((lead, i) => (
                <CompletedLeadRow
                  key={lead.id}
                  lead={lead}
                  rotation={BALL_ROTATIONS[i % BALL_ROTATIONS.length]}
                  onNavigate={() => onOpenChange(false)}
                />
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

type RowPhase = "crumpled" | "uncrumpling" | "revealed";

const FLAT_SHAPE = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
const CRUMPLED_SHAPE =
  "polygon(52% 2%, 78% 6%, 96% 22%, 100% 48%, 88% 74%, 70% 95%, 42% 100%, 14% 92%, 2% 68%, 0% 38%, 12% 14%, 32% 2%)";

function CompletedLeadRow({
  lead,
  rotation,
  onNavigate,
}: {
  lead: Lead;
  rotation: number;
  onNavigate: () => void;
}) {
  const [phase, setPhase] = useState<RowPhase>("crumpled");
  const completedAt = lead.project?.completed_at ?? null;

  const handleBallClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (phase !== "crumpled") return;
    setPhase("uncrumpling");
  };

  return (
    <li className={cn("group transition-colors", phase === "revealed" && "bg-[hsl(var(--primary)/0.03)]")}>
      <div className="flex items-start gap-3 px-6 py-3">
        {/* Crumpled ball — click to uncrumple */}
        <div
          onClick={handleBallClick}
          className={cn(
            "shrink-0 mt-0.5",
            phase === "crumpled" && "cursor-pointer",
            phase === "uncrumpling" && "cursor-wait",
            phase === "revealed" && "cursor-default",
          )}
          role={phase === "crumpled" ? "button" : undefined}
          aria-label={phase === "crumpled" ? `Uncrumple ${lead.business_name}` : undefined}
          tabIndex={phase === "crumpled" ? 0 : undefined}
          onKeyDown={(e) => {
            if (phase === "crumpled" && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              setPhase("uncrumpling");
            }
          }}
        >
          {phase === "crumpled" ? (
            <motion.div
              animate={{ rotate: rotation }}
              whileHover={{ scale: 1.18 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              title="Click to reveal"
            >
              <PaperBall size={28} rotation={0} />
            </motion.div>
          ) : phase === "uncrumpling" ? (
            <motion.div
              style={{
                width: 28,
                height: 28,
                background: `radial-gradient(circle at 33% 30%, #fefbf0 0%, #f0e2bc 55%, #d9c49a 100%)`,
                boxShadow: `1px 2px 5px rgba(0,0,0,0.18)`,
                overflow: "hidden",
                position: "relative",
              }}
              animate={{
                clipPath: [CRUMPLED_SHAPE, FLAT_SHAPE],
                borderRadius: ["50%", "3px"],
                scale: [1, 1.25],
              }}
              transition={{ duration: 0.34, ease: "easeOut" }}
              onAnimationComplete={() => setPhase("revealed")}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(120,90,30,0.06) 4px, rgba(120,90,30,0.06) 5px)",
                }}
              />
            </motion.div>
          ) : (
            /* revealed — flat paper icon */
            <motion.div
              initial={{ scale: 1.25, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 280, damping: 20 }}
              style={{
                width: 28,
                height: 28,
                background: `linear-gradient(135deg, #fefbf0, #f0e2bc)`,
                borderRadius: 3,
                boxShadow: `1px 2px 4px rgba(0,0,0,0.12)`,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(120,90,30,0.06) 4px, rgba(120,90,30,0.06) 5px)",
                }}
              />
            </motion.div>
          )}
        </div>

        {/* Content — hidden until revealed */}
        <div className="flex-1 min-w-0">
          <AnimatePresence mode="wait">
            {phase === "crumpled" || phase === "uncrumpling" ? (
              <motion.p
                key="hint"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-xs text-muted-foreground/50 mt-0.5"
              >
                Click the ball to reveal
              </motion.p>
            ) : (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                <p className="text-sm font-medium truncate">{lead.business_name}</p>
                <p className="text-xs text-muted-foreground">{lead.business_type}</p>
                <div className="mt-2 flex items-center gap-3">
                  <span className="text-xs text-muted-foreground/70">
                    Completed {formatCompletedDate(completedAt)}
                  </span>
                  <Link
                    href={`/leads/${lead.id}`}
                    onClick={onNavigate}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] hover:underline"
                  >
                    Open lead
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </li>
  );
}
